import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { useEffect, useState, type ComponentType } from 'react';
import {
  LayoutDashboard,
  TrendingUp,
  Users,
  CreditCard,
  Receipt,
  Mail,
  Newspaper,
  Sparkles,
  Mails,
  Heart,
  LifeBuoy,
  Map,
  Menu,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronLeft,
  type LucideProps,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { api } from '../lib/api';
import { cn } from '../lib/cn';

type Item = {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
};

const items: Item[] = [
  { to: '/admin',          label: 'Dashboard',       icon: LayoutDashboard },
  { to: '/admin/crecimiento', label: 'Crecimiento',   icon: TrendingUp },
  { to: '/admin/users',    label: 'Usuarios',         icon: Users },
  { to: '/admin/plans',    label: 'Planes',           icon: CreditCard },
  { to: '/admin/payment-logs', label: 'Logs de pagos', icon: Receipt },
  { to: '/admin/email-templates', label: 'Plantillas email', icon: Mail },
  { to: '/admin/blog',     label: 'Blog',             icon: Newspaper },
  { to: '/admin/autoblog', label: 'Autoblog',         icon: Sparkles },
  { to: '/admin/newsletter', label: 'Newsletter',     icon: Mails },
  { to: '/admin/roadmap',  label: 'Roadmap',          icon: Map },
  { to: '/admin/tickets',  label: 'Tickets soporte',  icon: LifeBuoy },
  { to: '/admin/health',   label: 'Salud técnica',    icon: Heart },
];

const COLLAPSE_KEY = 'zp:admin-sidebar:collapsed';

export function AdminLayout() {
  const { user, logout } = useAuth();

  const { data: openData } = useQuery({
    queryKey: ['admin', 'support', 'open-count'],
    queryFn: () => api<{ count: number }>('/api/admin/support/open-count'),
    refetchInterval: 30_000,
  });
  const openCount = openData?.count ?? 0;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  });

  useEffect(() => {
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  return (
    <div className="h-screen bg-slate-50 flex overflow-hidden">
      {open && (
        <button
          aria-label="Cerrar menú"
          onClick={() => setOpen(false)}
          className="lg:hidden fixed inset-0 bg-slate-900/40 z-30"
        />
      )}

      <aside
        className={cn(
          'fixed lg:static z-40 inset-y-0 left-0 bg-white border-r border-slate-200 flex flex-col transition-[width,transform] duration-200',
          'w-64',
          collapsed ? 'lg:w-16' : 'lg:w-64',
          'lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        {/* header */}
        <div
          className={cn(
            'h-16 border-b border-slate-200 flex items-center',
            collapsed ? 'lg:justify-center lg:px-0 px-4' : 'px-4 justify-between',
          )}
        >
          <Link to="/admin" className="flex items-center gap-2">
            <img
              src="/zetta-post-icon.png"
              alt="Zettapost"
              className="h-7 w-auto"
            />
            <span className={cn('text-sm font-semibold text-slate-800 tracking-tight', collapsed && 'lg:hidden')}>
              Admin
            </span>
          </Link>
          <button
            onClick={() => setCollapsed((c) => !c)}
            className={cn(
              'hidden lg:inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition',
              collapsed && 'lg:hidden',
            )}
          >
            <PanelLeftClose size={16} />
          </button>
        </div>

        {/* floating expand button */}
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            className="hidden lg:inline-flex absolute -right-3 top-[52px] h-6 w-6 items-center justify-center rounded-full bg-white border border-slate-200 text-slate-500 hover:text-brand-600 hover:border-brand-300 shadow-sm transition z-10"
          >
            <PanelLeftOpen size={12} />
          </button>
        )}

        {/* back to app */}
        <div className={cn('px-3 pt-3', collapsed && 'lg:px-2')}>
          <Link
            to="/dashboard"
            className={cn(
              'flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition',
              collapsed && 'lg:justify-center lg:px-0',
            )}
            title={collapsed ? 'Volver a la app' : undefined}
          >
            <ChevronLeft size={14} />
            <span className={cn(collapsed && 'lg:hidden')}>Volver a la app</span>
          </Link>
        </div>

        <nav className={cn('flex-1 px-3 py-3 space-y-0.5 overflow-y-auto', collapsed && 'lg:px-2')}>
          {items.map((item) => {
            const Icon = item.icon;
            const badge = item.to === '/admin/tickets' ? openCount : 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/admin'}
                onClick={() => setOpen(false)}
                title={collapsed ? (badge > 0 ? `${item.label} (${badge})` : item.label) : undefined}
                className={({ isActive }) =>
                  cn(
                    'flex items-center rounded-lg text-sm font-medium transition',
                    collapsed ? 'lg:justify-center lg:px-0 lg:py-2 lg:gap-0 px-3 py-2 gap-3' : 'px-3 py-2 gap-3',
                    isActive
                      ? 'bg-brand-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100',
                  )
                }
              >
                <span className="relative inline-flex">
                  <Icon size={17} />
                  {badge > 0 && (
                    <span className={cn(
                      'hidden absolute -top-1.5 -right-1.5 h-4 min-w-4 items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-1 ring-2 ring-white',
                      collapsed && 'lg:inline-flex',
                    )}>
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </span>
                <span className={cn('flex-1', collapsed && 'lg:hidden')}>{item.label}</span>
                {badge > 0 && (
                  <span className={cn(
                    'inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1.5',
                    collapsed && 'lg:hidden',
                  )}>
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className={cn('border-t border-slate-200', collapsed ? 'lg:p-2 p-3' : 'p-3')}>
          <div
            className={cn('px-3 py-1.5 text-xs text-slate-400 truncate', collapsed && 'lg:hidden')}
            title={user?.email}
          >
            {user?.email}
          </div>
          <button
            onClick={handleLogout}
            title={collapsed ? 'Salir' : undefined}
            className={cn(
              'btn-secondary w-full gap-2',
              collapsed ? 'lg:mt-0 mt-1 lg:px-0 lg:justify-center' : 'mt-1',
            )}
          >
            <LogOut size={16} />
            <span className={cn(collapsed && 'lg:hidden')}>Salir</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* mobile topbar */}
        <header className="lg:hidden h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-20 shrink-0">
          <button
            onClick={() => setOpen(true)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-700"
          >
            <Menu size={18} />
          </button>
          <Link to="/admin">
            <img src="/zetta-post-icon.png" alt="Admin" className="h-7 w-auto" />
          </Link>
          <span className="w-9" />
        </header>

        <main data-scroll-root className="flex-1 overflow-y-auto">
          <div className="px-6 py-8 max-w-7xl w-full mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
